// The revision of each annotation row this device has read (#1584; API contract
// `docs/next-steps-encryption-api.md` section 3, "Optimistic concurrency").
//
// WHY THIS EXISTS. An encrypted PATCH replaces the row's WHOLE payload column,
// so a client that re-states unchanged fields from its cache can undo an edit
// another device made to a field it never touched. The API's answer is a
// compare-and-set: every note, highlight and tag read carries a positive
// integer `revision`, an enrolled PATCH must send the one it last read as
// `expectedRevision`, and a stale write is refused with 409
// `ANNOTATION_REVISION_CONFLICT` having changed nothing.
//
// WHY A TRANSPORT-LEVEL MAP RATHER THAN A FIELD ON THE ROW. `revision` is not
// content — it is not in the envelope, it is not something a screen renders,
// and it is not what the member wrote. The store's cached shapes are explicit
// projections (`mapApiNote` names its fields, a tag object is
// `{ id, name, color, type }`), so carrying it as a row field would mean
// threading one more key through every projection, every mutation and every
// read cache, and would put a server bookkeeping number into the same
// localStorage the plaintext-cache guard exists to keep clean. Keeping it here
// means the read path records it once for every row that arrives and the write
// hook reads it once for every envelope update, and nothing in between has to
// know.
//
// IT IS DELIBERATELY NOT PERSISTED. A revision is only useful against the row
// the server holds now; a stale one from a previous launch would earn a 409 on
// the first write of every session. An empty map after a cold start is the
// correct state — the read that populates the screen also populates this.

/** `${type}:${itemId}` -> revision, for the life of one session. */
const revisions = new Map();

const keyFor = (type, itemId) => `${type}:${itemId}`;

/**
 * A revision the API would accept: a positive integer. The column is
 * `INTEGER NOT NULL DEFAULT 1` and the DTO is `@IsInt() @Min(1)`, so anything
 * else — absent on an older API, null on a projection that dropped it — is not
 * a revision and must not be remembered as one.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export function isRevision(value) {
  return Number.isInteger(value) && value >= 1;
}

/**
 * Remember what the server last said this row's revision was. Called for every
 * annotation row that arrives, including the echo of a write — a successful
 * PATCH answers with the row AFTER the increment, which is exactly what the
 * next write on the same row must send.
 *
 * @param {string} type one of `ANNOTATION_TYPE`
 * @param {string} itemId
 * @param {unknown} revision
 */
export function recordRevision(type, itemId, revision) {
  if (!type || !itemId || !isRevision(revision)) return;
  revisions.set(keyFor(type, itemId), revision);
}

/**
 * The revision to send as `expectedRevision`, or null when this device has not
 * read the row this session. Null is not a reason to guess: the API refuses an
 * envelope update with no precondition, and a guessed one would either be
 * refused or — worse, if it happened to match — overwrite an edit this device
 * never saw.
 *
 * @param {string} type
 * @param {string} itemId
 * @returns {number | null}
 */
export function revisionFor(type, itemId) {
  if (!type || !itemId) return null;
  return revisions.get(keyFor(type, itemId)) ?? null;
}

/**
 * Drop what is known about one row. Used when the server says this device's
 * revision is stale: the number that is wrong must not sit in the map waiting
 * to be sent again, and the refetch that follows will record the real one.
 *
 * @param {string} type
 * @param {string} itemId
 */
export function forgetRevision(type, itemId) {
  if (!type || !itemId) return;
  revisions.delete(keyFor(type, itemId));
}

/**
 * Forget every row. Logout commits this: the next member's rows have their own
 * revisions, and one member's numbers must never be sent against another's.
 */
export function forgetAllRevisions() {
  revisions.clear();
}

/** Test seam: how many rows are remembered. */
export function knownRevisionCount() {
  return revisions.size;
}

/**
 * The API's code for "you wrote against an older revision" (contract section
 * 5a). The annotation routes are the one place the contract attaches a code to
 * an otherwise uncoded 409, precisely so a client can branch on it.
 */
export const REVISION_CONFLICT_CODE = 'ANNOTATION_REVISION_CONFLICT';

/** What the member is told when their edit lost a race with another device. */
export const REVISION_CONFLICT_MESSAGE =
  'This changed on another device. Your edit was not saved — reopen it and try again.';

/**
 * Whether this failure is the stale-write refusal.
 *
 * BRANCHES ON THE CODE, NOT THE STATUS, and that is not fussiness: `PATCH
 * /api/tags/:id` answers 409 for a duplicate tag name too, with no code at all,
 * and the duplicate check runs BEFORE the revision compare — so a rename can be
 * both. Treating every 409 as a conflict would tell a member their tag changed
 * on another device when what actually happened is that they picked a name they
 * already use.
 *
 * @param {unknown} error an axios failure from the annotations transport
 * @returns {boolean}
 */
export function isStaleRevisionError(error) {
  const response = error?.response;
  return response?.status === 409 && response?.data?.code === REVISION_CONFLICT_CODE;
}

/**
 * Handle a write that lost the race: drop the revision this device believed,
 * so nothing sends that number again and the next read records the real one.
 * The 409 carries no current revision, so a refetch is the only way back — and
 * the caller must NOT replay the write, because an envelope replaces the whole
 * payload and a replay would destroy whatever the other device wrote.
 *
 * @param {string} type
 * @param {string} itemId
 */
export function onRevisionConflict(type, itemId) {
  forgetRevision(type, itemId);
}
